import React, { useState, useEffect, useContext } from 'react';
import { Layout, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { useLocalStorageState, useMount, useResponsive } from 'ahooks';
import styled from 'styled-components';
import { BellOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAccountingSetup } from 'Settings/hooks/accounting/setup';
import { LocaleContext } from 'utils/OwnIntlProvider';
import {
  getLeftTrialDays,
  getIsShorePayUser,
  getCustomerDisplayUser,
  getIsBetaUser,
  getGiftcardsV2User,
  getIsTrialUser,
  getIsShopUser,
  getIsDatevUser,
  getIsAdminUser,
} from 'Account/selectors';
import { useKSVProvisioningStatusCheck } from 'KSV/hooks/ksv';
import { useLogout } from 'Account/hooks/account';
import { useStoreSettings } from 'Settings/hooks/store/settings';
import { TssProvisionState } from 'utils/apiClient/services/ksv/types';
import {
  useLatestSubscription,
  usePortalSession,
} from 'Settings/hooks/business/subscription';
import { getIsContractAccepted } from 'Settings/utils';
import { ApiInfo } from 'Internal.CommonAPI';
import Loading from 'Common/Components/Loading';
import useLoginUserInfo from 'Common/hooks/loginuserinfo';
import SideMenu from './views/SideMenu';
import TopMenu, { ScreenTitle } from './views/TopMenu';
import ContentWrapper from './views/ContentWrapper';
import UserDropdown from './views/UserDropdown';
import IntegrationsDropdown from './views/IntegrationsDropdown';
import HomeTopBanner from './views/HomeTopBanner';

const CustomLayout = styled(Layout)`
  height: 100%;
  @media (max-width: 768px) {
    margin-left: 0 !important;
    width: 110%;
    height: 100vh;
  }
`;

const clearList = [
  'pre-accounting',
  'account',
  'products-table-params',
  'services-table-params',
  'giftcards-table-params',
  'datev-table-params',
  'categories-table-params',
  'undefined-table-params',
  'showCBSuccessModal',
  'newAppCredentials',
];

const Home: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const { locale: currentLanguage } = useIntl();
  const responsive = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const toggleMenu = () => setCollapsed(!collapsed);
  const screenName = (pathname.replace(/^\//g, '') ||
    'start-page') as ScreenTitle;
  const preAccountingQuery = useAccountingSetup();
  const queryClient = useQueryClient();
  const ksvStatusQuery = useKSVProvisioningStatusCheck();
  const orgToken: any = queryClient.getQueryState(['subscription', 'token']);
  const navigate = useNavigate();
  const accountData = (queryClient.getQueryData(['common', 'api']) as ApiInfo)
    ?.account;
  const [_, setPreAccounting] = useLocalStorageState<boolean>('pre-accounting');
  const [, setAccountingSetup] =
    useLocalStorageState<boolean>('accounting-setup');
  const context = useContext(LocaleContext);
  const portalSessionMutation = usePortalSession();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const logoutMutation = useLogout();
  const settingsQuery = useStoreSettings('/settings/store');
  const loginUserInfoQuery = useLoginUserInfo();
  const latestSubscriptionQuery = useLatestSubscription(
    orgToken?.data?.org_token,
    accountData?.external_id
  );

  useMount(() => {
    preAccountingQuery.refetch();
  });

  const isAccountActive: boolean = accountData?.permissions.includes(
    'subscriptions.can_use_inventorum'
  );
  const isPreAccountingUser = preAccountingQuery.isSuccess;

  useEffect(() => {
    setAccountingSetup(
      (preAccountingQuery.isSuccess && !preAccountingQuery.data.default) ||
        (preAccountingQuery.isError &&
          (preAccountingQuery.error as any)?.error?.key !==
            'account.permission')
    );
  }, [preAccountingQuery, setAccountingSetup]);

  useEffect(() => {
    if (!responsive.lg) {
      setCollapsed(true);
    }
  }, [responsive]);

  useEffect(() => {
    setPreAccounting(isPreAccountingUser);
  }, [isPreAccountingUser, setPreAccounting]);

  const signOut = () => {
    navigate('/login');
    clearList.forEach((item) => localStorage.removeItem(item));
  };

  const logout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => signOut(),
    });
  };

  const redirectActivate = () => {
    // Always redirect to Chargebee subscription management or settings
    if (
      accountData &&
      accountData?.subscriptions &&
      isAccountActive &&
      !getIsTrialUser(accountData?.subscriptions) &&
      typeof orgToken?.data?.org_token === 'string' &&
      typeof accountData?.external_id === 'string'
    ) {
      portalSessionMutation.mutate(
        {
          org_token: orgToken?.data?.org_token,
          merchant_id: accountData?.external_id,
        },
        {
          onSuccess: () => {
            setIsModalOpen(true);
          },
        }
      );
    } else {
      navigate('/settings/general?_=2');
    }
  };

  const switchLanguage = (lang: string) => {
    localStorage.setItem('locale', lang);
    context.selectLanguage(lang);
  };

  const renderModalContent = () => {
    if (portalSessionMutation.isPending) {
      return <Loading noText verticallyCentered />;
    }
    return (
      <iframe
        title="portal-session"
        src={portalSessionMutation.data?.access_url}
        width={360}
        height={530}
        style={{
          border: 'none',
        }}
      />
    );
  };

  return (
    <CustomLayout style={{ minHeight: '100vh' }}>
      <SideMenu
        screenName={screenName}
        collapsed={collapsed}
        isAccountActive={isAccountActive}
        accountSource={accountData?.source}
        country={accountData?.country}
        isShorePayUser={
          accountData !== undefined &&
          getIsShorePayUser(accountData?.permissions)
        }
        isCustomerDisplayUser={
          accountData !== undefined &&
          getCustomerDisplayUser(accountData?.permissions)
        }
        isBetaUser={
          accountData !== undefined && getIsBetaUser(accountData?.permissions)
        }
        isPreAccountingUser={
          !preAccountingQuery.isLoading && isPreAccountingUser
        }
        isGiftCardUser={
          accountData !== undefined &&
          getGiftcardsV2User(accountData?.permissions)
        }
        isShopUser={
          accountData !== undefined && getIsShopUser(accountData?.permissions)
        }
        isDatevUser={
          accountData !== undefined && getIsDatevUser(accountData?.permissions)
        }
      />
      <Layout
        style={{
          marginLeft: collapsed || window.innerWidth <= 768 ? 80 : 257,
          transition: 'all 0.2s',
        }}
      >
        <HomeTopBanner
          isAdminUser={getIsAdminUser(loginUserInfoQuery.data?.roles ?? [])}
          hasOverdueInvoices={
            latestSubscriptionQuery.isSuccess &&
            (latestSubscriptionQuery.data?.due_invoices_count ?? 0) > 0
          }
        />
        <TopMenu
          screenName={screenName}
          collapsed={collapsed}
          toggleMenu={toggleMenu}
          //@ts-ignore
          trialDaysLeft={getLeftTrialDays(accountData?.subscriptions)}
          handleActivateClick={redirectActivate}
          isAccountActive={isAccountActive}
          isfetchingData={ksvStatusQuery.isFetching || settingsQuery.isFetching}
          hasNotActiveTSE={
            accountData?.country === 'DE' &&
            isAccountActive &&
            !getIsTrialUser(accountData.subscriptions) &&
            (ksvStatusQuery.data?.provisioning_state ===
              TssProvisionState.NotActive ||
              ksvStatusQuery.data?.provisioning_state ===
                TssProvisionState.NoTSS ||
              !ksvStatusQuery.data?.can_use_ksv_v2)
          }
          isATUserWithMissingCountry={
            accountData?.country === 'AT' &&
            (settingsQuery.data?.billing_address === null ||
              settingsQuery.data?.billing_address?.country === null ||
              settingsQuery.data?.shipping_address === null ||
              settingsQuery.data?.shipping_address?.country === null)
          }
          isContractAccepted={getIsContractAccepted(latestSubscriptionQuery)}
        >
          <IntegrationsDropdown language={currentLanguage.toLowerCase()} />
          <BellOutlined
            id="beamer-widget"
            style={{
              color: 'rgba(0, 0, 0, .85)',
              opacity: '0.7',
              margin: '22px',
              fontSize: 19,
            }}
          />
          <UserDropdown
            accountName={accountData?.name as string}
            currentLanguage={currentLanguage}
            storeName={accountData?.store_name as string}
            accountMail={accountData?.email as string}
            switchLanguage={switchLanguage}
            handleLogout={logout}
          />
        </TopMenu>
        <ContentWrapper>{children}</ContentWrapper>
        <Modal
          width={430}
          open={isModalOpen}
          footer={<></>}
          closable
          destroyOnHidden
          onCancel={() => setIsModalOpen(false)}
        >
          {renderModalContent()}
        </Modal>
      </Layout>
    </CustomLayout>
  );
};

export default Home;
